
import React from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Trash2, Edit, Mail, MailCheck, Clock, AlertTriangle } from "lucide-react";
import StatusBadge from './StatusBadge';
import { getClientAuthStatus, getAuthStatusDisplay } from '@/services/admin/invitationService';

interface VerifiedClientsTableProps {
  verifiedClients: any[];
  handleRequestAction?: (request: any) => void;
  handleEditClient?: (client: any) => void;
  isProcessing: boolean;
  handleDeleteClient?: (request: any) => void;
  handleToggleActive?: (request: any) => void;
  handleSendInvitation?: (client: any) => void;
}

const VerifiedClientsTable: React.FC<VerifiedClientsTableProps> = ({
  verifiedClients,
  handleEditClient,
  isProcessing,
  handleDeleteClient,
  handleToggleActive,
  handleSendInvitation
}) => {
  console.log('VerifiedClientsTable rendered with:', {
    clientCount: verifiedClients.length,
    hasHandleEditClient: !!handleEditClient,
    hasHandleSendInvitation: !!handleSendInvitation,
    isProcessing,
    sampleClient: verifiedClients[0]
  });

  const handleEditClick = (client: any) => {
    console.log('=== EDIT BUTTON CLICKED ===');
    console.log('Client data:', client);
    console.log('Client type (Wave vs Regular):', client.wave_customer_id ? 'Wave Import' : 'Regular');
    console.log('handleEditClient function available:', !!handleEditClient);
    console.log('handleEditClient type:', typeof handleEditClient);
    
    if (!handleEditClient) {
      console.error('handleEditClient is not available!');
      return;
    }
    
    try {
      console.log('About to call handleEditClient...');
      handleEditClient(client);
      console.log('handleEditClient called successfully');
    } catch (error) {
      console.error('Error calling handleEditClient:', error);
    }
  };

  const renderInvitationButton = (client: any) => {
    const authStatus = getClientAuthStatus(client);
    
    switch (authStatus) {
      case 'wave_import':
        return (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleSendInvitation?.(client)}
            disabled={isProcessing}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
          >
            <Mail className="h-4 w-4 mr-1" />
            Send Invitation
          </Button>
        );
      
      case 'invitation_sent':
        return (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleSendInvitation?.(client)}
            disabled={isProcessing}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <MailCheck className="h-4 w-4 mr-1" />
            Resend
          </Button>
        );
      
      case 'invitation_expired':
        return (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleSendInvitation?.(client)}
            disabled={isProcessing}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            Resend Expired
          </Button>
        );
      
      case 'registered':
      default:
        return null;
    }
  };

  const renderAuthStatus = (client: any) => {
    const authStatus = getClientAuthStatus(client);
    const { text, color } = getAuthStatusDisplay(authStatus);
    
    const getIcon = () => {
      switch (authStatus) {
        case 'registered':
          return <MailCheck className="h-4 w-4" />;
        case 'invitation_sent':
          return <Clock className="h-4 w-4" />;
        case 'invitation_expired':
          return <AlertTriangle className="h-4 w-4" />;
        case 'wave_import':
        default:
          return <Mail className="h-4 w-4" />;
      }
    };

    return (
      <div className={`flex items-center gap-1 ${color}`}>
        {getIcon()}
        <span className="text-sm">{text}</span>
      </div>
    );
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableCaption>List of verified clients</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Contact Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Verified Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Active</TableHead>
            <TableHead>Auth Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {verifiedClients.map((client) => (
            <TableRow key={client.id} className={!client.is_active ? "bg-gray-50 opacity-70" : ""}>
              <TableCell className="font-medium">{client.company_name || '-'}</TableCell>
              <TableCell>{client.full_name}</TableCell>
              <TableCell>{client.email}</TableCell>
              <TableCell>{client.phone || '-'}</TableCell>
              <TableCell>{new Date(client.updated_at).toLocaleDateString()}</TableCell>
              <TableCell><StatusBadge status={client.status} /></TableCell>
              <TableCell>
                {handleToggleActive && (
                  <Switch 
                    checked={client.is_active} 
                    onCheckedChange={() => handleToggleActive(client)}
                    disabled={isProcessing}
                  />
                )}
              </TableCell>
              <TableCell>
                {renderAuthStatus(client)}
              </TableCell>
              <TableCell className="flex gap-2 flex-wrap">
                {handleEditClient && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditClick(client)}
                    disabled={isProcessing}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
                {handleSendInvitation && renderInvitationButton(client)}
                {handleDeleteClient && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteClient(client)}
                    disabled={isProcessing}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {verifiedClients.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                No verified clients found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default VerifiedClientsTable;
